/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState } from "react";
import { Select, MenuItem, FormControl } from "@mui/material";
import { ReactComponent as CircleArrowDown } from "../../assets/icons/circle-arrow-down.svg";
import { ReactComponent as CircleArrowUp } from "../../assets/icons/circle-arrow-up.svg";
import { ReactComponent as CircleDropdown } from "../../assets/icons/circle-dropdown.svg";

const defaultMunicipalityOptions = [
    { value: "All municipalities", label: "All municipalities" },
    { value: "Bowen Island Municipality", label: "Bowen Island Municipality" },
    { value: "District of North Vancouver", label: "District of North Vancouver" },
    { value: "District of West Vancouver", label: "District of West Vancouver" },
    { value: "Village of Lions Bay", label: "Village of Lions Bay" }
];

const Dropdown = (props) => {
    const currentValue = props?.value || "all";
    const dropdownOptions = props?.options || defaultMunicipalityOptions;
    const placeholderText = props?.placeholder || "Select an option";
    const isDisabled = props?.disabled || false;
    const shouldUseFullWidth = props?.fullWidth !== undefined ? props?.fullWidth : true;
    const inputVariant = props?.variant || "outlined";
    const inputSize = props?.size || "medium";
    const dropdownWidth = props?.width || "400px";
    const onChangeHandler = props?.onChange;

    // Extract passthrough props by removing known props
    const knownProps = ["value", "onChange", "options", "placeholder", "disabled", "fullWidth", "variant", "size", "width"];
    const passthroughProps = {};
    Object.keys(props || {}).forEach((key) => {
        if (!knownProps?.includes(key)) {
            passthroughProps[key] = props?.[key];
        }
    });

    const [selectedValue, setSelectedValue] = useState(currentValue);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Styling variables
    const selectInputStyles = {
        height: "56px",
        borderRadius: "3px",
        border: "1px solid #1B2E4B",
        background: "#FFFFFF",
        paddingLeft: "16px",
        paddingRight: "16px",
        cursor: "pointer",
        "& .MuiSelect-select": {
            color: "#151515",
            fontFamily: "Montserrat",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "normal",
            letterSpacing: "0.1px",
            paddingLeft: 0,
            paddingRight: "40px",
            cursor: "pointer"
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none"
        },
        "& .MuiSelect-icon": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            top: "50%",
            transform: "translateY(-50%)",
            right: "16px"
        }
    };

    const dropdownMenuStyles = {
        PaperProps: {
            sx: {
                borderRadius: "0 0 3px 3px",
                borderRight: "1px solid #1B2E4B",
                borderBottom: "1px solid #1B2E4B",
                borderLeft: "1px solid #1B2E4B",
                borderTop: "none",
                background: "#FFFFFF",
                boxShadow: "none",
                "& .MuiMenuItem-root": {
                    color: "#151515",
                    fontFamily: "Montserrat",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "normal",
                    letterSpacing: "0.1px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    height: "56px",
                    cursor: "pointer",
                    "&:hover": {
                        background: "#1B2E4B",
                        color: "#FFFFFF"
                    },
                    "&.Mui-selected": {
                        background: "transparent !important",
                        color: "#151515",
                        "&:hover": {
                            background: "transparent !important",
                            color: "#151515"
                        }
                    }
                }
            }
        }
    };

    const handleSelectionChange = (event) => {
        const selectedOptionValue = event?.target?.value;
        setSelectedValue(selectedOptionValue);
        if (onChangeHandler) {
            onChangeHandler(event);
        }
    };

    const renderSelectedValue = (currentSelectedValue) => {
        if (!currentSelectedValue || currentSelectedValue === "") {
            return <span style={{ color: "#9ca3af" }}>{placeholderText}</span>;
        }
        const matchingOption = dropdownOptions?.find((option) => option?.value === currentSelectedValue);
        return matchingOption?.label || currentSelectedValue;
    };

    return (
        <FormControl
            fullWidth={shouldUseFullWidth}
            variant={inputVariant}
            size={inputSize}
            sx={{ width: dropdownWidth, height: "56px" }}
        >
            <Select
                value={selectedValue}
                onChange={handleSelectionChange}
                displayEmpty
                disabled={isDisabled}
                open={isDropdownOpen}
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
                IconComponent={isDropdownOpen ? CircleArrowUp : CircleArrowDown}
                sx={selectInputStyles}
                MenuProps={dropdownMenuStyles}
                renderValue={renderSelectedValue}
                {...passthroughProps}
            >
                {dropdownOptions?.map((optionItem) => (
                    <MenuItem key={optionItem?.value} value={optionItem?.value}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <span>{optionItem?.label}</span>
                            {selectedValue === optionItem?.value && (
                                <CircleDropdown style={{ marginLeft: "8px" }} />
                            )}
                        </div>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export { Dropdown };
